import styled, { css } from 'styled-components';

import { colors, layout, media, colorUtils } from '@prisjakt/ui-base';

import {
  speedToDurationMs,
  Speed,
  Direction,
} from '@regular_ui/lib/definitions';
import { Hideable } from '@regular_ui/lib/components/hideable';
import { slideHideableVariant } from '@prisjakt/customization_prisjakt/variants/hideable';
import ToggleIcon from '../icons/toggle-sidebar';

const padding = layout.spacing.xSmall;
const contentWidth = layout.sidebarContentWidth.desktop;
const menuWidth = layout.sidebarMenuWidth.desktop;

const pageWithSidebarContent =
  layout.pageContent.maxWidth.value +
  layout.pageContent.gutter.value +
  (contentWidth.value + menuWidth.value) * 2;

type SidebarProps = {
  tab: 'none' | 'lists' | 'alerts' | 'filter';
};

export const Sidebar = styled.div<SidebarProps>`
  display: flex;
  flex-shrink: 0;
  width: ${props =>
    props.tab === 'none'
      ? menuWidth.rem
      : `calc(${contentWidth.rem} + ${menuWidth.rem})`};
  transition: ${p => `width ${speedToDurationMs[Speed.Slow]}ms ease`};
  grid-row: 1 / span 2;

  /* Reserve space between page content and sidebar menu to properly align page content */
  /* Debug or see how it works by setting a background color to this component */
  @media ${`(min-width: ${pageWithSidebarContent}px)`} {
    width: calc(${contentWidth.rem} + ${menuWidth.rem});
  }
  @media ${`(max-width: ${pageWithSidebarContent}px)`} {
    min-width: calc((100vw - ${layout.pageContent.maxWidth.rem}) / 2);
  }

  ${media.mediumDown`
    display: none;
  `};
`;

type StickyProps = {
  hasOldMenu: boolean;
};
export const Sticky = styled.div<StickyProps>`
  display: flex;
  position: fixed;
  height: calc(100vh - ${layout.headerHeight.desktop.rem});
  z-index: 11;

  ${p =>
    p.hasOldMenu &&
    css`
      ${media.largeUp`
        position: sticky;
        bottom: 0;
        top: ${layout.headerHeight.desktop.value}px;
      `};
    `};
`;

export const SidebarMenu = styled.div`
  display: flex;
  flex-direction: column;
  flex-shrink: 0;
  align-items: center;
  width: ${menuWidth.rem};
  background-color: ${colors.semiWhite};
  padding: ${padding} 0;
  z-index: 99;
`;

export const SidebarContent = styled.div.attrs({ role: 'dialog' })`
  position: relative;
  flex-shrink: 0;
  width: ${contentWidth.rem};
  border-right: 1px solid ${p => p.theme.lines.primary};
  padding: ${padding};
  overflow: auto;
  overscroll-behavior: contain;
  background: ${p => p.theme.bg.primary};
  z-index: 10;
  flex-direction: column;
`;

type MenuButtonProps = {
  active?: boolean;
  hoverEffect?: 'background' | 'color';
  hidden?: boolean;
};
export const MenuButton = styled.button<MenuButtonProps>`
  cursor: pointer;
  display: flex;
  justify-content: center;
  align-items: center;
  width: 40px;
  height: 40px;
  border-radius: 20px;
  margin-left: auto;
  margin-right: auto;
  transition: background 200ms ease, color 200ms ease, visibility 0s ease 0s,
    opacity 200ms;
  background: transparent;
  color: ${p => p.theme.icons.tertiary};
  visibility: visible;
  opacity: 1;

  rect,
  path {
    fill: ${props =>
      props.active ? props.theme.icons.callToAction : 'currentColor'};
  }

  ${p =>
    p.hidden &&
    css`
      visibility: hidden;
      opacity: 0;
      transition: visibility 0s ease 200ms, opacity 200ms;
    `}

  &:hover,
  &.focus-visible {
    ${p =>
      p.hoverEffect === 'background' &&
      css`
        background-color: ${p.active
          ? colorUtils.hexToRgbaString(p.theme.icons.callToAction, 0.2)
          : colorUtils.hexToRgbaString(p.theme.icons.tertiary, 0.2)};
      `};

    ${p =>
      p.hoverEffect === 'color' &&
      css`
        color: ${p.theme.icons.callToAction};
      `};
  }
`;
MenuButton.defaultProps = {
  hoverEffect: 'background',
};

export const HideableDialog = styled(Hideable).attrs({
  variant: slideHideableVariant,
  styledComponentProps: {
    slideDirection: Direction.Left,
  },
})``;

export const TopBar = styled.div`
  > * {
    margin-top: ${padding};
    margin-bottom: ${padding};
  }
`;

type BottomBarProps = {
  hasOldMenu: boolean;
};
export const BottomBar = styled.div<BottomBarProps>`
  position: relative;

  ${p =>
    p.hasOldMenu &&
    css`
      position: sticky;
      bottom: 0;
    `};

  margin-top: auto;
  display: flex;
  flex-direction: column;
  align-items: center;
  padding-bottom: ${padding};
  padding-top: ${padding};

  > * {
    margin-top: ${padding};
    margin-bottom: ${padding};
  }
`;

export const SidebarHeader = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
  width: 100%;
  padding: ${padding};
`;

export const Title = styled.div`
  font-weight: bold;
  text-overflow: ellipsis;
  white-space: nowrap;
  overflow: hidden;
  padding: 0 ${layout.spacing.xxSmall};
`;

export const SettingsIcon = styled(({ component }) => component)`
  path {
    fill: ${p => p.theme.icons.tertiary} !important;
  }
`;

export const GoBackButton = styled.div`
  width: 34px;
  height: 34px;
`;

export const SettingsButton = styled.div`
  /* Reserve space for a button to center align text */
  width: 34px;
  height: 34px;
`;

export const OptionButton = styled.button`
  width: 100%;
  text-align: left;
  white-space: nowrap;
  padding: ${layout.spacing.xSmall};
  cursor: pointer;
  transition: ${layout.animations.buttonTransition};

  &:hover {
    background: ${p => p.theme.dropdowns.secondary.hoverBackground};
  }
`;

type ToggleSidebarIconProps = {
  open: boolean;
};
export const ToggleSidebarIcon = styled(ToggleIcon)<ToggleSidebarIconProps>`
  transition: transform 200ms ease;
  transform: ${p => (p.open ? 'rotate(180deg)' : 'none')};
`;

export const Divider = styled.span`
  pointer-events: none;
  background: ${p => p.theme.lines.primary};
  height: 1px;
  width: 75%;
`;
